<template>
  <section v-once class="section row align-center">
    <header class="columns small-12 large-8">
      <h1 class="text-primary">Privacy Policy</h1>
    </header>

    <div class="columns small-12 large-8">
      <h3>Part One</h3>
      <h2>1. Privacy Policy</h2>
      <p>
        Teach Starter Pty Ltd ABN 69 634 768 817
        <strong>(Teach Starter)</strong>
        values the privacy of the Personal Information of Users of this website and all related
        subdomains
        <strong>(Users)</strong>
        located at
        <a href="https://www.teachstarter.com" title="Teach Starter website">
          www.teachstarter.com
        </a>
        <strong>(Site)</strong>
        as well as related iOS and Android Apps and other applications, and is committed to handling
        a User’s Personal Information in a responsible way in accordance with the Privacy Act 1988
        (Cth)
        <strong>(Act)</strong>
        and the Australian Privacy Principles
        <strong>(APP)</strong>
        as set out in the Act.
      </p>
      <p>
        Teach Starter also makes every effort to comply with laws and regulations of the United
        States, including but not limited to California law, including the California Consumer
        Privacy Act (“CCPA”), Cal. Civ. Code 1798.100: U.S. Children’s Online Privacy Protection Act
        (“COPPA”); PRIVO’s Kids Privacy Assured COPPA Safe Harbor Certification Program (“the
        Program”) requirements; Calif. Bus. &amp; Prof. Code §§ 22580-22582; Nevada NRS § 603A.300;
        Vermont 9 V.S.A § 2446- 2447 (Protection of Personal Information: Data Brokers;
        Delaware-Del. Code § 1204C; Oregon- ORS § 646.607;
      </p>
      <p>
        For ease of reference a separate Privacy Policy complying with relevant United States
        Federal and State laws and regulations are set forth below in Part Two of this Policy and a
        Children’s Privacy Policy is set forth in Part Three.
      </p>
      <h3>1.1 This Privacy Policy:</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          sets out how Teach Starter collects, holds, uses and discloses the Personal Information of
          Users, and
        </li>
        <li>
          should be read in conjunction with and forms part of Teach Starter’s&nbsp;
          <a :href="'/' + $i18n.locale + '/about/terms-and-conditions/'">
            <strong>Terms and Conditions</strong>
          </a>
          when using the Site and interacting with Teach Starter.
        </li>
      </ol>
      <h3>1.2 Meaning of Personal Information</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          The term “Personal Information” and some other terms used in this Privacy Policy are
          defined under the Act.
        </li>
        <li>
          <strong>Personal Information</strong>
          means information or an opinion about an identified individual, or an individual who is
          reasonably identifiable:
          <ol style="list-style-type: lower-roman">
            <li>whether the information or opinion is true or not; and</li>
            <li>whether the information or opinion is recorded in a material form or not.</li>
          </ol>
        </li>
        <li>
          If information does not disclose the User’s identity or enable the User’s identity to be
          ascertained, it will in most cases not be classified as Personal Information and will not
          be subject to this Privacy Policy.
        </li>
      </ol>
      <h3>1.3 Meaning of Personal Information</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          The kind of Personal Information that Teach Starter collects from the User will depend on
          how the User uses the Site.
        </li>
        <li>
          Personal Information Teach Starter collects may include a person’s first and last names,
          their email address, other contact details, IP address, location data and a User’s school,
          details of products and services supplied to Users or which Users have enquired about,
          details needed to deliver those products and services, details about a User’s account,
          information about use of Teach Starter’s services, information about Users that they
          provide Teach Starter directly or indirectly when online, together with any other Personal
          Information supplied by the User (subject to clause 1.3(d) &amp; (e) below.
        </li>
        <li>
          If a User connects Teach Starter to a third party social media service, Teach Starter may
          collect additional Personal Information that includes social media profile handles, user
          ID and/or user names associated with that service and any information or content the User
          has permitted the social media service to share with Teach Starter, which may include the
          User’s profile picture, email address, friends or follower lists, and information the User
          has disclosed in connection with that social media services.
        </li>
        <li>
          Teach Starter does not collect sensitive information about Users as defined under the Act.
        </li>
        <li>
          Teach Starter will not knowingly collect or use Personal Information from or about a minor
          where the law requires parental consent, If it does so unknowingly Teach Starter will use
          its best endeavours to obtain parental consent. Parents may review the Personal
          Information which has been collected, refuse permission to collect further Personal
          Information and request that any Personal Information collected be deleted from Teach
          Starter’s records.
        </li>
      </ol>
      <h3>1.4 How we collect and hold Personal Information</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter may collect Personal Information from Users whenever they input such
          information into the Site or through interaction with social media services where they
          have authorised that information to be shared.
        </li>
        <li>
          Teach Starter also collects information via browser cookies where reasonably necessary for
          the performance of Teach Starter’s operations and activities including marketing and
          sales.
        </li>
        <li>
          Teach Starter automatically receives and records information on its server logs from the
          User’s browser, including their IP address, operating system, top level domain, date and
          time, pages accessed, documents downloaded, previous website visited, type of browser used
          and other cookie information.
        </li>
        <li>
          This enables Teach Starter to tell when Users use the Site and also to help customise the
          User’s experience within the Site.
        </li>
        <li>
          Users are always advised to pay attention to their browser’s security and privacy settings
          to protect their privacy generally.
        </li>
        <li>
          Teach Starter will only solicit and collect Personal Information which has been obtained
          fairly and lawfully, directly from a User (unless unreasonable or impractical to do so)
          and is reasonably necessary for Teach Starter’s operations.
        </li>
        <li>
          If Teach Starter receives unsolicited Personal Information about Users, Teach Starter will
          determine within a reasonable period if it would have been permitted to request such
          Personal Information under APP3. If so, Teach Starter may use and treat that information
          as if it had collected it as solicited Personal Information.
        </li>
        <li>
          If Teach Starter receives unsolicited Personal Information about Users and Teach Starter
          determines it could not have collected the Personal Information under APP3, it will
          destroy or de‑identify the Personal Information as soon as practicable.
        </li>
        <li>Clause 2 below describes how Teach Starter holds Personal Information.</li>
      </ol>
      <h3>1.5 Purpose of collection and use</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter collects, holds, uses and discloses Personal Information to deliver and
          improve the goods or services Teach Starter provides to Users.
        </li>
        <li>
          Generally, Users are only obliged to provide Teach Starter with information necessary for
          Teach Starter to provide services to them.
        </li>
        <li>
          If Users do not provide Teach Starter with certain types of Personal Information Teach
          Starter may be unable to provide them with the services or goods they have requested.
        </li>
        <li>
          Teach Starter customarily discloses Personal Information only to its employees,
          contractors and service providers who assist it in operating Teach Starter and the Site.
          Personal Information may also be exposed from time to time to maintenance and support
          personnel acting in the normal course of their duties.
        </li>
        <li>
          Teach Starter may use Personal Information to promote goods and services to Users and by
          using the Site, Users consent to the receipt of direct marketing material.
        </li>
        <li>
          Teach Starter will only use User’s Personal Information for that purpose if it has
          collected such information directly from Users and if it is material which a User would
          reasonably expect to receive from Teach Starter.
        </li>
        <li>
          Teach Starter does not use sensitive Personal Information in direct marketing activity and
          does not direct market to minors.
        </li>
        <li>
          Teach Starter’s direct marketing material will include a simple unsubscribe function by
          which a User can request not to receive further communications of that nature.
        </li>
        <li>
          Where practical and reasonable to do so, Teach Starter provides Users with the opportunity
          to use pseudonyms in relation to information Users provide to Teach Starter.
        </li>
        <li>
          Where appropriate Teach Starter will provide Users with the option in to interact
          anonymously.
        </li>
      </ol>
      <h3>1.6 Accessing User’s Personal Information</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          If a User would like to access or correct Personal Information that Teach Starter holds
          about them, please contact:
          <br />
          Teach Starter Pty Ltd ABN 69 634 768 817
          <br />
          Level 2, 55 Railway Terrace
          <br />
          Milton,&nbsp;QLD 4064
          <br />
          Email:&nbsp;
          <a href="about:blank">support@teachstarter.com</a>
        </li>
        <li>
          The User will need to identify themselves to the reasonable satisfaction of Teach Starter
          before Teach Starter will provide the User with their Personal Information which Teach
          Starter may or may not have in its possession.
        </li>
        <li>
          If a User requests a correction of Personal Information, Teach Starter will endeavour to
          acknowledge receipt within seven days and provide a written response within 30 days.
        </li>
      </ol>
      <h3>1.7 Complaints</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter has put in place an effective process to resolve privacy complaints and
          enquiries. All complaints and enquiries will be dealt with in a reasonably appropriate
          timeframe in a way which does not compromise the integrity of any decision about a
          complaint.
        </li>
        <li>
          Teach Starter operates a service orientated business via the Site. &nbsp;If a User has a
          complaint about Teach Starter’s collection or use of Personal Information pertaining to
          that User, then the User is requested to contact Teach Starter’s&nbsp;
          <strong>Privacy Officer&nbsp;</strong>
          at first instance. The Privacy Officer may seek further information from the User to
          clarify their concerns and, if the complaint is well founded, will, in consultation with
          the User, take appropriate steps to rectify the problem.
        </li>
        <li>
          If the User is dissatisfied with the outcome, they may refer the matter to the Office of
          the Australian Information Commissioner in writing:
          <ul>
            <li>
              by email to
              <a href="about:blank">enquiries@oaic.gov.au</a>
              ;
            </li>
            <li>by mail to GPO Box 5218, Sydney NSW 2001; or</li>
            <li>by facsimile to (02) 9284 9666.</li>
          </ul>
        </li>
        <li>
          If Users have questions about how to make a complaint, they should telephone the enquiries
          line of the OAIC on 1300 363 992.
        </li>
        <li>
          For further information about lodging a complaint, Users should go to
          <a href="about:blank">https://www.oaic.gov.au/privacy/privacy-complaints/</a>
          .
        </li>
      </ol>
      <h3>1.8 Transfer of Personal Information outside Australia</h3>
      <ol style="list-style-type: lower-alpha">
        <li>
          Users’ Personal Information collected by Teach Starter may be accessed by or transferred
          to employees, contractors or service providers outside Australia, including in Singapore,
          the United States of America and the United Kingdom.
        </li>
        <li>
          If Teach Starter discloses Personal Information to an overseas recipient, it will only
          disclose the Personal Information for the primary purpose for which it was collected,
          subject to any exceptions under the Act.
        </li>
        <li>
          Users who have supplied or authorised the supply of Personal Information to Teach Starter
          expressly consent to the disclosure of Personal Information to such overseas recipients
          and understand the potential consequences of doing so.
        </li>
        <li>
          Teach Starter will use reasonable endeavours to ensure that the overseas location has data
          protection laws which protect Personal Information in a way which is substantially similar
          to the APP. Teach Starter also requires overseas recipients to comply with the APP and
          will be accountable for any acts of an overseas recipient that would breach the APP.
        </li>
        <li>
          Teach Starter will obtain signed and enforceable undertakings by its employees,
          contractors, service providers and other recipients outside Australia to comply with the
          terms of this Privacy Policy and the APP and will only use recognised service providers
          which use enterprise level software with up‑to‑date SSL or similar encryption protocols.
        </li>
      </ol>
      <h2>2. Integrity of Personal Information</h2>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter takes reasonable steps to protect the Personal Information Teach Starter
          holds from misuse and loss and from unauthorised access, modification or disclosure
          including when Teach Starter sends Personal Information offshore.&nbsp; Further, Teach
          Starter takes reasonable steps to destroy or permanently de-identify Personal Information
          if it is no longer needed for the purpose it was collected.
        </li>
        <li>
          Teach Starter takes reasonable steps to ensure that the Personal Information Teach Starter
          collects, uses or discloses is accurate, complete, relevant and up-to-date.
        </li>
        <li>
          Teach Starter has developed up‑to‑date procedures and policies for information security
          and digital security is incorporated as an integral component of Teach Starter’s risk
          management programs.
        </li>
        <li>
          Teach Starter will comply with the requirements of the Notifiable Data Breaches Scheme
          under the Act, including:
          <ol style="list-style-type: lower-roman">
            <li>
              identifying relevant data breaches and whether the data breach would be likely to
              result in serious harm to a User whose Personal Information was part of the data
              breach,
            </li>
            <li>notification to Users who are possibly affected and the relevant authority,</li>
            <li>
              instituting remedial action, make recommendations about steps that Users should take
              and any remedial action which Teach Starter has taken or may in future take in
              relation to the data breach.
            </li>
          </ol>
        </li>
        <li>
          In some circumstances the European Union (EU) General Data Protection Regulation (GDPR)
          provides additional protection to individuals located in Europe. The fact that Users may
          be in Europe does not on its own entitle them to protection under the GDPR. The Site does
          not specifically target Users located in the EU and Teach Starter does not monitor the
          behaviour of individuals in the EU and therefore the GDPR does not apply.
        </li>
      </ol>
      <h2>3. Changes to Privacy Policy</h2>
      <ol style="list-style-type: lower-alpha">
        <li>This Privacy Policy is up to date as at 17 April 2020.</li>
        <li>
          There may be changes to the Privacy Policy from time to time and Users should check
          regularly for those changes.
        </li>
      </ol>
      <hr />
      <h3>Part Two</h3>
      <h2>1. Privacy Policy Applicable To The United States</h2>
      <ol>
        <li>
          If you reside in the United States, Part Two hereof describes our obligations and your
          rights under various State and Federal Laws relating to Privacy, said laws collectively
          referred to herein as ”Privacy Laws”).
        </li>
        <li>
          All of the terms, conditions and definitions set forth in our Terms and Conditions as well
          as Paragraphs 1 through 3 above, as they apply to Australia are integrated herein by
          reference with the same force and effect as if specifically set forth. To the extent that
          any of the foregoing are inconsistent with the Privacy Laws, the terms and conditions in
          Part Two shall take precedence.
        </li>
      </ol>
      <p><strong>Pursuant this Privacy Policy you have the following rights:</strong></p>
      <h3>1.1 Rights to Access and Delete Your Personal Information</h3>
      <p>
        You have the right (i) to have access to categories Personal Information and specific pieces
        of Personal Information about you that we collect, use, and disclose, and (ii) to request
        that we delete Personal Information that we collect from you, subject to any applicable
        legal exceptions in force at the time of such request. As used herein, “Personal
        Information” shall have the meaning set forth in Section 1.2 with examples provided in
        Section 1.3(b)
      </p>
      <p>
        Requests for access and/or deletion should be directed to our Security Officer utilizing the
        following Contact Information:
      </p>
      <p>
        Teach Starter Pty Ltd
        <br />
        ABN 69 634 768 817
        <br />
        Level 2, 55 Railway Terrace
        <br />
        Milton, QLD 4064
        <br />
        Australia
        <br />
        Email: support@teachstarter.com
      </p>
      <p>
        For security purposes we may require a verification of your identity and shall send you a
        link with verification requests, such as your email address. Please note that you will not
        receive any discriminatory treatment for the exercise of your rights you exercise within
        this Policy.
      </p>
      <h3>1.2 Right to Opt Out of Sale of Personal Information to Third Parties</h3>
      <p>
        <strong>
          We do not sell your Personal Information and it is our present plan not to sell your
          Personal Information.
        </strong>
        Should this change, you will be notified and you will have the right to opt out (simply tell
        us you no longer wish us to sell your Personal Information) “Third Parties” means and
        includes types of entities that do not collect personal information directly from consumers,
        including but not limited to advertising agencies.”
      </p>
      <h3>1.3 Additional Rights Under Our Privacy Policy</h3>
      <p>
        Under our Privacy Policy you shall have the right to request us to provide you with
        information with respect to our disclosure of your Personal Information by our company to
        third parties to use for direct marketing purposes.
        <strong>
          Note, however that we do not share Personal Information with third parties engaged in
          direct marketing unless you have given us permission to do so.
        </strong>
        To make such a request please contact our Security Officer utilizing the Contact Information
        above.
      </p>
      <h3>1.4 Removal of Content</h3>
      <p>
        If you are a California resident under the age of 18, and a user of any site where this
        policy is posted, California Business and Professions Code Section 22581 permits you to
        request and obtain removal of content or information you have publicly posted. In this
        regard, please contact our Security Officer with a detailed description of the content or
        information you wish removed. Note that while we will make every effort to remove such
        content there may be instances where complete compliance is not possible. If you are a
        resident of a state other than California you are granted the same rights as California
        residents.
      </p>
      <p>
        The types of Personal Information we have collected in the last 12 months is set forth in
        1.3(b) In the future, we may expand the type of Personal Information to include:
        Identifiers/Contact Information, demographic information (like gender and age), payment card
        information associated with you, commercial information, Internet or other electronic
        network activity information, geolocation data, audio, electronic, visual or similar
        information, and inferences drawn from the above.
      </p>
      <p>
        In the future, we may disclose the following categories of personal information for a
        business purpose: Identifiers/Contact Information, demographic information (like gender and
        age), payment card information associated with you, commercial information, Internet or
        other electronic network activity information, geolocation data, audio, electronic, visual
        or similar information, and inferences drawn from the above. We may disclose such categories
        to advertising networks, data analytics providers, and social networks. The business or
        commercial purposes of disclosing Personal Information is for third party companies to
        perform services on our behalf, like marketing, advertising, and audience measurement.
      </p>
      <p>
        <strong>
          We do not disclose Personal Information of known minors under 16 years of age.
        </strong>
      </p>
      <hr />
      <h3>Part Three</h3>
      <h2>1. Children’s Privacy Policy</h2>
      <p>
        This Children’s Online Privacy Policy sets forth information which is relevant to Personal
        Information provided by children under the age of 12 (“child” or “children”). All of our
        Terms &amp; Conditions, the terms of our Privacy Policy and the Terms of our Privacy Policy
        Applicable to the United States are integrated and incorporated herein by reference.
      </p>
      <p>
        This policy is in accordance with the U.S. Children’s Online Privacy Protection Act
        (“COPPA”), and outlines our practices in the United States. For more information about COPPA
        and general tips about protecting children’s online privacy, please visit the Federal Trade
        Commission’s link
        <a href="https://www.consumer.ftc.gov/articles/0031-protecting-your-childs-privacy-online">
          OnGuard Online
        </a>
        , “Protecting Your Child’s Privacy Online”
      </p>
      <p>
        When we collect Personal Information from a child, namely anyone below the age of 13, we
        shall hold that information only so long as, in our view, it is reasonably necessary to
        fulfill or engage in certain activities that we offer or allow the child to continue to
        participate in such activities. In this regard it is our aim and ensure the security of such
        Personal Information and that our practices are compliant with existing laws and
        regulations. If we learn that we have collected information from a child in a manner
        inconsistent with COPPA’s requirements, we will either delete the information or immediately
        request the parent’s consent for that collection.
      </p>
      <h3>1.1 Registration</h3>
      <p>
        Children can, in many cases, register on our website. In this process it is possible that we
        may ask the child to provide certain information for notification and security purposes,
        including a parent or guardian’s email address, the child’s first name and gender, the
        child’s username, and password, birth dates from children to validate their ages. Children
        can choose whether to share their information with us, but certain features cannot function
        without it. As a result, children may not be able to access certain features if required
        information has not been provided. We strongly recommend that registration by a child be
        closely supervised by a parent or a guardian
      </p>
      <p>
        When we determine that the user is age 13 or under, we will request that the child or a
        parent or guardian supervising the child provide the parent’s or guardian’s email address
        before we collect any personal information from the child. If you believe your child is
        participating in an activity that collects personal information and you or another
        parent/guardian have NOT received an email providing notice or seeking your consent, please
        feel free to contact us via our Security Officer, contact information above.
      </p>
      <h3>1.2 Content Generated by a Child</h3>
      <p>
        Whenever an activity potentially allows a child to insert personal information in their
        created content, we will use our best efforts to either pre-screen the submission to delete
        any personal information, or we will seek verifiable parental consent by email for the
        collection.
      </p>
      <h4>About Verifiable Parental Consent:</h4>
      <p>
        <strong>Email Consent.</strong>
        In the event Teach Starter wishes to collect personal information from a child, COPPA
        requires that we first seek a parent or guardian’s consent by email. In the email we will
        explain what information we are collecting, how we plan to use it, how the parent can
        provide consent, and how the parent can revoke consent. If we do not receive parental
        consent within a reasonable time, we will delete the parent contact information and any
        other information collected from the child in connection with that activity.
      </p>
      <p>
        <strong>Teacher consent in lieu of a parent.</strong>
        With regard to school-based activities, COPPA allows teachers and school administrators to
        act in the place of parents to provide consent for the collection of personal information
        from children. Schools should always notify parents about these activities.
      </p>
      <h3>1.3 Email Contact with a Child</h3>
      <p>
        On occasion, in order to respond to a question or request from a child, Teach Starter may
        need to ask for the child’s online contact information, such as an email address. Teach
        Starter will delete this information immediately after responding to the question or
        request.
      </p>
      <p>
        In connection with certain activities or services, we may collect a child’s online contact
        information, such as an email address, in order to communicate with the child more than
        once. In such instances we will retain the child’s online contact information to honor the
        request and for no other purpose such as marketing.
      </p>
      <p>
        The following activities will always require the child to provide a parent’s email address,
        (i) the ability to receive push notifications, and/or (ii) geolocation information. Once we
        have such email we shall contact the parent or guardian for permission in regard to the two
        foregoing activities.
      </p>
      <p><strong>Exceptions to Parental Approvals</strong></p>
      <p>
        There may be certain instances where, of necessity, we may have to share or disclose
        Personal Information regarding your child and you acknowledge that in such instances we have
        the right to do so:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>
          Pursuant to a court order, subpoena, a request from law enforcement or governmental
          agency,
        </li>
        <li>
          If in our opinion the disclosure would prevent a criminal act or assist in the public
          welfare or relate to the safety of a child
        </li>
        <li>To enable our service providers to perform business or support services for us.</li>
        <li>To insure the child’s welfare and safety, for example the prevention of a crime</li>
        <li>To insulate us from potential liability</li>
      </ol>
      <p>
        At any time, parents can refuse to permit us to collect further personal information from
        their children in association with a particular account, and can request that we delete from
        our records the personal information we have collected in connection with that account.
        Please keep in mind that a request to delete records may lead to a termination of an
        account, membership, or other service.
      </p>
      <p>
        Where a child has registered for a Teach Starter account, parents can request access or
        deletion of information in the child’s account by addressing a request to our Security
        Officer, contact information above. When contacting our Security Officer please include the
        child’s username and the parent’s email address and telephone number. To protect children’s
        privacy and security, we will take reasonable steps to help verify a parent’s identity
        before granting access to any personal information.
      </p>
    </div>
  </section>
</template>

<script>
/* Privacy Policy */

export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      title: 'Teach Starter Privacy Policy',
      description:
        'This document sets out how Teach Starter collects, holds, uses and discloses the Personal Information of Users',
    };
  },
  head() {
    return {
      title: this.title,
      meta: [
        { hid: 'title', name: 'title', content: this.title },
        {
          hid: 'description',
          name: 'description',
          content: this.description,
        },
      ],
      link: this.$hrefLangsPage(this.$route),
    };
  },
};
</script>

<style scoped lang="scss">
h1 {
  color: $ts-green-500;
}

h2 {
  font-size: $font-size-800;
}
</style>
